<script setup>
import { computed } from 'vue'

import ProductIcon from '@/components/icons/ProductIcon.vue'
import StepperIcon from '@/components/icons/StepperIcon.vue'

// #region - prop definitions
const props = defineProps({
  itemClass: {
    type: String,
    required: false,
    default: ''
  },
  cardBodyClass: {
    type: String,
    required: false,
    default: ''
  },
  status: {
    type: String,
    required: false,
    default: null
  },
  message: {
    type: String,
    required: false,
    default: ''
  },
  enableIcon: {
    type: Boolean,
    required: false,
    default: true
  },
  showIcon: {
    type: Boolean,
    required: false,
    default: true
  },
  cardFooterClass: {
    type: String,
    required: false,
    default: 'border-0'
  },
  isCardFooterActive: {
    type: Boolean,
    required: false,
    default: false
  },
  messageClass: {
    type: String,
    required: false,
    default: ''
  },
  cardContentClass: {
    type: String,
    required: false,
    default: ''
  }
})
// endregion

// #region - computed props
const classAccordingToStatus = computed(() => {
  const statusObj = {
    info: {
      content: 'content-status-info px-3 py-4',
      title: 'title-status-info',
      icon: 'mdi-information-outline'
    },
    success: {
      content: 'content-status-success px-3 py-4',
      title: 'title-status-success'
    },
    warning: {
      content: 'content-status-warning px-3 py-4',
      title: 'title-status-warning',
      icon: 'mdi-information-outline'
    },
    danger: {
      content: 'content-status-danger px-3 py-4',
      title: 'title-status-danger',
      icon: 'mdi-alert-outline'
    },
    default: {
      content: 'content-status-default shadow-none px-1 py-1',
      title: 'title-status-default',
      icon: ''
    }
  }
  return statusObj[props.status] || statusObj.default
})
// endregion

</script>

<template>
  <div class="card d-block position-relative" :class="itemClass">
    <div class="card-body" :class="cardBodyClass">
      <template v-if="status">
        <div :class="classAccordingToStatus.title" class="d-flex align-items-center title-content">
          <template v-if="showIcon === true">
            <i v-if="enableIcon && status !== 'success'" :class="`mdi ${classAccordingToStatus.icon}`" class="text-2xl" />
            <stepper-icon v-else-if="status === 'success'" class="me-2" />
            <product-icon v-else />
          </template>
          <p class="status-success-title ms-1 mb-0 me-2" :class="messageClass" v-if="message">{{ message }}</p>
          <slot name="rights-side" />
        </div>
        <hr class="hr" />
        <div class="card d-block my-2" :class="cardContentClass ? cardContentClass : classAccordingToStatus.content">
          <slot />
        </div>
      </template>

      <template v-else>
        <slot />
      </template>
      <slot name="content" />

      <div class="card-footer" :class="cardFooterClass" v-if="isCardFooterActive === true">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<style>
.card {
  &.status {
    border-radius: 12px;
  }

  &.content-status-info {
    background: #ebf9fe;
  }

  &.content-status-success {
    background: #eaf6ed;
  }

  &.content-status-warning {
    background: #fef7e7;
  }

  &.content-status-danger {
    background: #fbede9;
  }

  &.content-status-default {
    background: #ffffff;
  }

  &.disabled {
    opacity: 0.55;
    pointer-events: none;
  }

  .hr {
    border-color: #eff0f0;
  }

  .title-content {
    margin-bottom: -8px;
  }

  .title-status-info,
  .title-status-success,
  .title-status-warning,
  .title-status-danger,
  .title-status-default {
    & p {
      font-weight: 600;
      font-size: 23px;
    }
  }

  .title-status-info {
    & i,
    & p {
      color: #207490;
    }
  }

  .title-status-success {
    & i,
    & p {
      color: #1e652d;
    }
  }

  .title-status-warning {
    & i,
    & p {
      color: #faad13;
    }
  }

  .title-status-danger {
    & i,
    & p {
      color: #ab391f;
    }
  }

  .title-status-default {
    & i,
    & p {
      color: var(--secondary);
    }
  }
}
</style>
